/**
 * API client dla 3 endpointów listingu instancji procesów (F17 T17.1.6 v3).
 *
 * Osobny plik od `api.ts` (definicje) — różna domena. Patrz ADR-0040.
 *
 * Endpointy:
 *   - `GET /api/processes/my-pending-decisions` — step-level (kroki przypisane
 *     do bieżącego usera). Commit 2 z enrichment.
 *   - `GET /api/processes/my-pending-decisions/section` — wiersze listy jednej kartoteki
 *     z krokami decyzji (sekcja „Grupuj według: Kartoteka").
 *   - `GET /api/processes/my-submitted-instances` — instance-level (instancje
 *     zgłoszone przez bieżącego usera). Commit 3.
 *   - `GET /api/processes/instances/all` — instance-level (cross-user, gated
 *     permission `processes.decisions.view_all`). Commit 4.
 */

import {
  type AvailableRoute,
  type DecisionGroupBy,
  type DecisionPriority,
  type NoApproversStage,
  PROCESS_BULK_DECISIONS_MAX_ITEMS,
} from '@saas/shared';
import { isAxiosError } from 'axios';
import type { TFunction } from 'i18next';

import type { TableLayoutConfig } from '@/components/configurable-table';
import type { ModuleField } from '@/components/fields/FieldRegistry';
import { api } from '@/lib/api';
import { extractErrorCode } from '@/lib/errors';
import type { ExtRefLabels } from '@/lib/ext-list-columns';

export type { AvailableRoute, DecisionGroupBy, DecisionPriority };

export type InstanceStatus = 'active' | 'completed' | 'cancelled' | 'suspended' | 'error';

/**
 * Wspólne pola enriched response (te same dla 3 endpointów). Backend dorzuca
 * te 4 pola batch enrichment helperem `buildEnrichmentLookups`.
 */
export interface EnrichedSubmitterInfo {
  id: string;
  displayName: string | null;
  email: string;
  username: string;
}

/** Kwota rekordu docelowego (kolumna „Wartość"). null = brak kwoty dla klasy. */
export interface TargetAmount {
  /** numeric(14,2) jako string — formatowanie na froncie bez utraty precyzji. */
  amount: string;
  currencyCode: string;
}

/** Suma „Wartość na liście" dla jednej waluty. */
export interface CurrencySum {
  currencyCode: string;
  /** Suma jako string z 2 miejscami po przecinku, np. "300.00". */
  total: string;
}

// ============================================================================
// GET /api/processes/my-pending-decisions — moje czekające decyzje
// ============================================================================

/** Step-level row — kroki gdzie aktualny user jest akceptantem. */
export interface MyPendingDecisionRow {
  stepInstanceId: string;
  instanceId: string;
  nodeId: string;
  stepStatus: 'pending' | 'in_progress';
  assignedToUserIds: string[];
  createdAt: string;
  instanceNumber: number;
  processId: string;
  targetModuleCode: string;
  targetRecordId: string;
  instanceSubmittedAt: string;
  companyId: string | null;
  submittedByUserId: string;
  // Enrichment (Commit 2):
  processName: string | null;
  currentNodeLabel: string | null;
  targetLabel: string | null;
  submittedByUser: EnrichedSubmitterInfo | null;
  // Drogi aktualnego etapu (≥2 → masowy wybór drogi per wniosek, T17.8).
  /** Drogi dostępne na etapie, w kolejności wyświetlania (domyślna pierwsza). */
  availableRoutes: AvailableRoute[];
  /**
   * Czy w oknie „Akceptacja" tego kroku jest dostępna opcja „Cofnij do poprzedniego etapu" (liczy
   * serwer) — wiersz pokazuje wtedy pozycję w menu „…". Zablokowanie cofnięcia (brak dostępnych
   * osób) okno wyjaśni samo, po otwarciu.
   */
  stepBackAllowed: boolean;
  /** Kwota kolumny „Wartość" — null gdy cel nie jest dokumentem lub klasa bez kwoty. */
  amount: TargetAmount | null;
  /** Nazwa kontrahenta rekordu docelowego (kolumna „Kontrahent") — null gdy cel nie ma kontrahenta. */
  counterpartyName: string | null;
  /** Kwota netto rekordu docelowego (kolumna „Netto") — null gdy cel nie jest dokumentem lub klasa bez netto. */
  netAmount: TargetAmount | null;
  /** Kwota brutto rekordu docelowego (kolumna „Brutto") — null gdy cel nie jest dokumentem lub klasa bez brutto. */
  grossAmount: TargetAmount | null;
  /**
   * Nazwa firmy REKORDU, którego sprawa dotyczy (skrócona, gdy brak — pełna; kolumna „Firma") —
   * null tylko wtedy, gdy serwer nie ustali firmy rekordu.
   */
  companyName: string | null;
  /** Priorytet/pilność wniosku (`process_instances.priority`) — patrz `DECISION_PRIORITIES` w `@saas/shared`. */
  priority: DecisionPriority;
  /** Termin (SLA, migracja 0126) — `process_instances.deadline_at`. `null` = bez terminu. */
  deadlineAt: string | null;
  /**
   * Czy termin minął i automat eskalował krok (`process_step_instances.escalated`): przełożony
   * akceptanta dostał mail, a decyzja przeszła na niego albo nie (bez prawa decyzji w firmie
   * rekordu — tylko powiadomienie). Który wariant — wpis eskalacji w historii sprawy.
   */
  escalated: boolean;
  /** Kiedy nastąpiła eskalacja (`process_step_instances.escalated_at`). `null` = brak. */
  escalatedAt: string | null;
}

export interface MyPendingDecisionsResponse {
  data: MyPendingDecisionRow[];
  total: number;
  limit: number;
  offset: number;
  /** Suma „Wartość na liście" per waluta — po WSZYSTKICH stronach, dedup dokumentu. */
  sumsByCurrency: CurrencySum[];
}

/**
 * Zakres widoczności decyzji: `mine` = kroki przypisane do bieżącego użytkownika, `team` = kroki jego
 * bezpośrednich podwładnych (widok tylko do odczytu). Wspólny typ zapytań listy, nagłówków koszyków,
 * rozwinięć koszyków i sekcji kartotek oraz zakładki „Moje czekające".
 */
export type PendingDecisionScope = 'mine' | 'team';

export interface MyPendingDecisionsQuery {
  limit?: number;
  offset?: number;
  /**
   * Kreator Listy — filtr per kolumna z popupu nagłówka `MyPendingDecisionsTab`.
   * JSON.stringify `HeaderFilter[]` (mirror `serializeHeaderFiltersForRequest` w
   * `InstanceListingTable.tsx`). Whitelist backendu →
   * `HEADER_FILTER_FIELDS` w `apps/api/app/api/processes/my-pending-decisions/route.ts`.
   */
  headerFilters?: string;
  /**
   * Sort listy w formacie `<pole>:<kier>` (pole ∈ instance_number | submitted_at |
   * target_module_code | priority | deadline_at | counterparty | company | net_amount |
   * gross_amount | …; kier ∈ asc | desc). Whitelist backendu →
   * `sortFields` w `apps/api/app/api/processes/my-pending-decisions/route.ts`.
   */
  sort?: string;
  /**
   * Filtr „tylko przeterminowane" (`deadline_at < now()`, server-authoritative) —
   * zasila kafelek „Przeterminowane" i przełącznik nad tabelą. Czytany wprost z
   * query przez backend (poza whitelistą `headerFilters`), patrz route.ts.
   */
  overdue?: boolean;
  /**
   * Zakres widoczności — `mine` (domyślnie, parametr pomijany w żądaniu) = kroki
   * przypisane do bieżącego usera; `team` = kroki bezpośrednich podwładnych
   * przełożonego (widok TYLKO-PODGLĄD, zasila kafelek „Zespół" na kokpicie).
   * Backend zwraca 403 `FORBIDDEN` gdy user nie ma uprawnienia „podgląd zespołu".
   */
  scope?: PendingDecisionScope;
  /**
   * Firma wybrana w górnym pasku (`companyIdParam(useActiveCompanyScope())`) — zawęża do spraw,
   * których rekord należy do tej firmy. Brak = tryb „Wszystkie firmy".
   */
  companyId?: string;
}

export async function fetchMyPendingDecisions(
  query: MyPendingDecisionsQuery = {},
): Promise<MyPendingDecisionsResponse> {
  const params: Record<string, string> = {};
  if (query.limit !== undefined) params.limit = String(query.limit);
  if (query.offset !== undefined) params.offset = String(query.offset);
  if (query.headerFilters !== undefined) params.headerFilters = query.headerFilters;
  if (query.sort !== undefined) params.sort = query.sort;
  if (query.overdue) params.overdue = 'true';
  if (query.scope === 'team') params.scope = 'team';
  if (query.companyId) params.companyId = query.companyId;
  const res = await api.get<MyPendingDecisionsResponse>('/processes/my-pending-decisions', {
    params,
  });
  return res.data;
}

// ============================================================================
// GET /api/processes/<endpoint>?groupBy=<wymiar> — tryb grupowany (grupowanie
// server-side, fala 2 kokpitu decyzji). WSPÓLNY kontrakt dla wszystkich 3
// endpointów listingu (`my-pending-decisions`, `my-submitted-instances`,
// `instances/all`) — obecność `groupBy` w query BEZ `groupKey` przełącza kształt
// odpowiedzi z płaskiego na NAGŁÓWKI KOSZYKÓW (agregacja SQL, bez wierszy —
// `runDecisionGrouping` w `apps/api/lib/processes/decisions-list/grouping.ts`);
// `groupBy` + `groupKey` razem → ROZWINIĘCIE koszyka: zwykła płaska odpowiedź
// (`data`/`total`/`limit`/`offset`, jak bez grupowania) zawężona do tego koszyka
// przez `?groupKey=` — front dociąga ją LENIWIE, dopiero po rozwinięciu nagłówka
// (patrz `<Tab>.tsx`, sekcja „grupowanie"). Wymiary → `DECISION_GROUP_BY` +
// białe listy per ekran (`PENDING_DECISION_GROUP_BY`/`SUBMITTED_INSTANCE_GROUP_BY`/
// `ACTIVE_INSTANCE_GROUP_BY`) w `@saas/shared`.
// ============================================================================

/**
 * Klucz koszyka „brak wartości" w odpowiedzi grupowanej — stały string `'__none__'` (patrz
 * `DecisionGroupHeader.key`). Lista opcji filtra „Firma" pomija ten koszyk: firma to firma
 * rekordu, a każdy rekord ją ma, więc na „Moje czekające" takiego koszyka nie powinno być
 * (pomijamy defensywnie).
 */
export const DECISION_GROUP_NONE_KEY = '__none__';

/**
 * Nagłówek koszyka grupowanej odpowiedzi — BEZ wierszy (te dociąga osobne żądanie z
 * `groupKey`, patrz komentarz nagłówkowy sekcji). Kształt WSPÓLNY dla wszystkich 3 endpointów.
 */
export interface DecisionGroupHeader {
  /**
   * Klucz koszyka — stabilny (id procesu/usera/firmy, enum priorytetu/statusu/statusu terminu,
   * `documents:<klasa>` lub kod modułu dla `target_class`, etykieta kroku dla `current_node`).
   * Koszyk „brak wartości" (wiersz bez wartości w tym wymiarze) niesie stały string
   * `'__none__'` — wysyłaj go z powrotem jako `groupKey` DOSŁOWNIE przy rozwinięciu (React key
   * + wejście do mapowania etykiet, patrz `groupLabelText` w `InstanceListingTable.tsx`).
   */
  key: string;
  /**
   * Etykieta „danych" (nazwa procesu/składającego/firmy, etykieta kroku) — `null` gdy
   * nierozpoznana LUB gdy koszyk to `'__none__'` (front pokazuje fallback „(brak)"). Dla
   * wymiarów enumeratywnych bez joina (priority/deadline_status/status/target_class) backend
   * zwraca tu SUROWY klucz — front tłumaczy przez i18n.
   */
  label: string | null;
  count: number;
  /**
   * Suma kwot koszyka per waluta — dedup po dokumencie W OBRĘBIE koszyka (mirror `sumsByCurrency`
   * całości). Puste `[]` na ekranach bez kwoty (`my-submitted-instances`/`instances/all` — te
   * ziarno nie ma kolumny „Wartość", backend liczy `withAmountSums: false`).
   */
  sumsByCurrency: CurrencySum[];
}

// ---- „Moje czekające" (step-level) ----------------------------------------

/**
 * Nagłówek koszyka „Moje czekające" — wspólny nagłówek + liczba REKORDÓW. `count` to liczba
 * KROKÓW decyzji (jeden rekord może mieć kilka otwartych kroków, np. gałęzie równoległe), a
 * `recordCount` — liczba rekordów (kartotek) w koszyku: to ona jest licznikiem w nagłówku sekcji
 * kartoteki (`DecisionSections`), bo tabela sekcji ma jeden wiersz na rekord, nie na krok. Pole
 * niosą tylko nagłówki tego endpointu (`my-submitted-instances`/`instances/all` nie mają kroków
 * decyzji), dlatego osobny typ zamiast opcjonalnego pola we wspólnym `DecisionGroupHeader`.
 */
export interface PendingDecisionGroupHeader extends DecisionGroupHeader {
  recordCount: number;
}

export interface GroupedPendingDecisionsResponse {
  groups: PendingDecisionGroupHeader[];
  groupBy: DecisionGroupBy;
  /** Liczba wierszy WCHODZĄCYCH do grupowania (suma `count` wszystkich koszyków) — bez paginacji. */
  total: number;
}

export interface GroupedPendingDecisionsQuery {
  groupBy: DecisionGroupBy;
  /** Mirror `MyPendingDecisionsQuery.headerFilters` — ortogonalne do grupowania (backend filtruje, potem grupuje). */
  headerFilters?: string;
  /** Mirror `MyPendingDecisionsQuery.overdue`. */
  overdue?: boolean;
  /** Mirror `MyPendingDecisionsQuery.scope`. */
  scope?: PendingDecisionScope;
  /** Mirror `MyPendingDecisionsQuery.companyId`. */
  companyId?: string;
}

/**
 * Nagłówki koszyków `GET /api/processes/my-pending-decisions?groupBy=` (bez `groupKey`) — BEZ
 * paginacji, backend agreguje SQL-em WSZYSTKIE pasujące wiersze w liczności/sumy per koszyk.
 * Wiersze koszyka dociąga `fetchPendingDecisionGroupRows` PO rozwinięciu (leniwie).
 */
export async function fetchGroupedPendingDecisions(
  query: GroupedPendingDecisionsQuery,
): Promise<GroupedPendingDecisionsResponse> {
  const params: Record<string, string> = { groupBy: query.groupBy };
  if (query.headerFilters !== undefined) params.headerFilters = query.headerFilters;
  if (query.overdue) params.overdue = 'true';
  if (query.scope === 'team') params.scope = 'team';
  if (query.companyId) params.companyId = query.companyId;
  const res = await api.get<GroupedPendingDecisionsResponse>('/processes/my-pending-decisions', {
    params,
  });
  return res.data;
}

export interface PendingDecisionGroupRowsQuery {
  groupBy: DecisionGroupBy;
  /** Klucz koszyka do rozwinięcia — z `DecisionGroupHeader.key`, wysyłany DOSŁOWNIE (patrz JSDoc tam). */
  groupKey: string;
  limit?: number;
  offset?: number;
  headerFilters?: string;
  overdue?: boolean;
  scope?: PendingDecisionScope;
  /** Sort wierszy koszyka (fala 2) — mirror `MyPendingDecisionsQuery.sort`. */
  sort?: string;
  /** Mirror `MyPendingDecisionsQuery.companyId`. */
  companyId?: string;
}

/**
 * Rozwinięcie JEDNEGO koszyka `GET /api/processes/my-pending-decisions?groupBy=&groupKey=` —
 * zwykła płaska, paginowana odpowiedź (identyczny kształt co `fetchMyPendingDecisions`),
 * zawężona do wierszy tego koszyka. Wołane LENIWIE — dopiero gdy user rozwinie nagłówek koszyka
 * (patrz `MyPendingDecisionsTab.tsx`).
 */
export async function fetchPendingDecisionGroupRows(
  query: PendingDecisionGroupRowsQuery,
): Promise<MyPendingDecisionsResponse> {
  const params: Record<string, string> = { groupBy: query.groupBy, groupKey: query.groupKey };
  if (query.limit !== undefined) params.limit = String(query.limit);
  if (query.offset !== undefined) params.offset = String(query.offset);
  if (query.headerFilters !== undefined) params.headerFilters = query.headerFilters;
  if (query.overdue) params.overdue = 'true';
  if (query.scope === 'team') params.scope = 'team';
  if (query.companyId) params.companyId = query.companyId;
  if (query.sort !== undefined) params.sort = query.sort;
  const res = await api.get<MyPendingDecisionsResponse>('/processes/my-pending-decisions', {
    params,
  });
  return res.data;
}

// ---- Sekcja kartoteki („Grupuj według: Kartoteka") --------------------------
// `GET /api/processes/my-pending-decisions/section` — wiersze listy JEDNEJ kartoteki (np. faktury
// zakupu, klucz grupy `documents:purchase_invoice`), każdy z krokami decyzji, które czekają na
// użytkownika. Wiersz = wiersz listy kartoteki (te same klucze camelCase co `GET /purchase-invoices`)
// + `decisions`. Serwer ignoruje parametry spoza poniższych; zakres „Zespół" (`scope=team`) zwraca
// `decisions` jak dla „moich", tylko dla podwładnych (panel pokazuje je wtedy tylko do odczytu; brak
// uprawnienia podglądu zespołu → 403), nieznany klucz → 400 `UNKNOWN_SECTION`, moduł nieaktywny → 402.
// Dokumenty Symfonii Handel (klucze `handel:<rodzaj>`) mają osobny, stały kształt wiersza —
// `HandelSectionRow`; na `/section/distinct` z takim kluczem serwer odpowiada 400
// `DISTINCT_NOT_SUPPORTED`, więc panel o podpowiedzi wartości dla Handlu nie pyta.
// Kartoteki własne (klucze `custom.<slug>`) mają wiersze listy kartoteki (wartości pól pod slugiem
// pola, pola złożone jako obiekty) i niosą w odpowiedzi katalog kolumn (`columns`, patrz
// `CustomSectionColumns`) — panel nie woła wtedy ani katalogu pól, ani układu, ani `?columns=`.

/**
 * Sumy kolumn kwot sekcji (`sums=1`): slug kolumny → suma w KAŻDEJ walucie osobno (waluty rosnąco,
 * `total` = dokładny tekst dziesiętny). Kolumna bez żadnej kwoty ma `[]`, a sekcja bez kolumn kwot —
 * cały obiekt pusty (`{}`). Slugi: faktury `amount_net` / `amount_vat` / `amount_gross`, umowy
 * `contract_value`, Handel sprzedaż i zamówienia `net_value` / `gross_value`, magazyn `net_value` /
 * `document_value`. Sumy liczone są po filtrach sekcji, ze WSZYSTKICH stron.
 */
export type DecisionSectionSums = Record<string, CurrencySum[]>;

/** Firma rekordów sekcji — pozycja listy wyboru filtra „Firma" (`companyOptions=1`). */
export interface DecisionSectionCompanyOption {
  id: string;
  name: string;
}

/**
 * Kategoria kartoteki własnej w odpowiedzi sekcji — to, czego potrzebuje lista: zakładki, kolumna
 * „Kategoria" (filtr z listą kategorii) i numeracja (`numberingConfig.enabled`).
 */
export interface CustomSectionCategory {
  id: string;
  name: string;
  slug: string;
  color: string;
  icon: string;
  isDefault: boolean;
  showAsTab: boolean;
  sortOrder: number;
  numberingConfig: { enabled: boolean } | null;
}

/**
 * Katalog kartoteki własnej w odpowiedzi jej sekcji (`columns`, tylko klucze `custom.<slug>`) —
 * to, z czego panel buduje kolumny i wiersz filtrów jak na liście tej kartoteki, bez prawa do
 * niej:
 *  - `fields`: aktywne pola (kształt pola z `GET /api/modules/:id`, z opcjami i regułami
 *    walidacji), BEZ pól „Hasło" i „Ukryte" — serwer ich nie zwraca (panel i tak odrzuca je po
 *    typie),
 *  - `layout`: aktywny układ listy (wiersz układu; panel czyta z niego `listConfig`) albo `null`
 *    (brak klucza = jak `null`),
 *  - `categories`: kategorie rekordów, `hasNumbering`: czy któraś ma numerację („Numer").
 */
export interface CustomSectionColumns {
  fields: ModuleField[];
  layout?: { listConfig: Partial<TableLayoutConfig> | null } | null;
  categories: CustomSectionCategory[];
  hasNumbering: boolean;
}

/**
 * Odpowiedź sekcji: wiersze kartoteki z krokami decyzji + metadane sortu/filtra silnika list.
 * `sums` i `companyOptions` dochodzą tylko wtedy, gdy panel o nie poprosił (`sums=1`,
 * `companyOptions=1`); `columns` niosą wyłącznie sekcje kartotek własnych (`custom.<slug>`).
 */
export interface DecisionSectionResponse<TRow extends object> {
  items: Array<TRow & { id: string; decisions: MyPendingDecisionRow[] }>;
  /** Liczba REKORDÓW pasujących do filtrów (nie kroków) — po wszystkich stronach. */
  total: number;
  limit: number;
  offset: number;
  /** Slugi kolumn sortowalnych / filtrowalnych serwerowo (autorytatywne, jak `meta` list kartotek). */
  meta: { sortable: string[]; filterable: string[] };
  /** Etykiety pól-odwołań (sibling wierszy) — brak klucza = odwołanie nierozwiązywalne (brak dostępu). */
  extRefLabels?: ExtRefLabels;
  /** Sumy kolumn kwot (tylko przy `sums=1`) — patrz `DecisionSectionSums`. */
  sums?: DecisionSectionSums;
  /**
   * Firmy rekordów sekcji, po nazwie (tylko przy `companyOptions=1`). Serwer liczy je BEZ filtrów
   * nagłówka, więc lista nie zawęża się przy filtrowaniu innych kolumn.
   */
  companyOptions?: DecisionSectionCompanyOption[];
  /**
   * Katalog kartoteki własnej (tylko sekcje `custom.<slug>`) — patrz `CustomSectionColumns`. Dla
   * takiego klucza serwer wysyła go zawsze; `null` to złamany kontrakt (panel pokazuje wtedy błąd
   * sekcji, patrz `isCustomSectionCatalog`), zestawy bez katalogu w ogóle nie niosą klucza.
   */
  columns?: CustomSectionColumns | null;
}

/**
 * Wiersz sekcji dokumentów Symfonii Handel (klucze grup `handel:sale` / `handel:warehouse` /
 * `handel:order`): `id` to id POWIĄZANIA dokumentu z procesem (nie id dokumentu w Symfonii), a
 * nawigacja do dokumentu idzie przez nie (`use-navigate-to-target`). Odpowiedź sekcji Handlu nie
 * niesie `extRefLabels`, a parametr `columns` serwer ignoruje. Brak pola `status`: stan liczy
 * się z `inBuffer` (`true` = bufor, `false` = wystawiony, `null` = nieznany).
 */
export interface HandelSectionRowBase {
  id: string;
  documentNumber: string | null;
  inBuffer: boolean | null;
  /** Data wystawienia, `YYYY-MM-DD`. */
  issueDate: string | null;
  contractorName: string | null;
  netValue: string | null;
  typeCode: string | null;
  /**
   * Waluta dokumentu — zawsze kod (nigdy `null`): wiersz bierze ją z kopii powiązania
   * `hm_document_links`, a ta kolumna jest `NOT NULL` (pusta waluta Symfonii znaczy PLN, patrz
   * `toLinkCache`); silnik obiegu zamraża w sprawie cały wiersz powiązania. Dzięki temu każda kwota
   * w sekcji ma walutę, tak jak sumy „Razem" (serwer pomija wiersze bez waluty).
   */
  currencyCode: string;
  companyId: string | null;
  companyName: string | null;
  decisions: MyPendingDecisionRow[];
}

/** Wiersz sprzedaży i zamówień — kwota brutto. */
export interface HandelGrossSectionRow extends HandelSectionRowBase {
  grossValue: string | null;
}

/** Wiersz magazynu — wartość dokumentu zamiast brutto. */
export interface HandelWarehouseSectionRow extends HandelSectionRowBase {
  documentValue: string | null;
}

export type HandelSectionRow = HandelGrossSectionRow | HandelWarehouseSectionRow;

export interface DecisionSectionQuery {
  /** Klucz grupy z nagłówka koszyka (`DecisionGroupHeader.key`), np. `documents:purchase_invoice`. */
  key: string;
  /** Mirror `MyPendingDecisionsQuery.overdue` — zakres „Przeterminowane". */
  overdue?: boolean;
  /** Mirror `MyPendingDecisionsQuery.scope` — `team` = decyzje podwładnych (tylko podgląd); brak/`mine` = moje. */
  scope?: PendingDecisionScope;
  limit?: number;
  offset?: number;
  /** Slug kolumny sortu (osobno od `order`, jak na liście faktur). */
  sort?: string;
  order?: 'asc' | 'desc';
  /** JSON `HeaderFilter[]` — ten sam kształt co na liście faktur. */
  headerFilters?: string;
  /** Slugi kolumn rozdzielone przecinkiem (projekcja odpowiedzi; sekcje Handlu je ignorują). */
  columns?: string;
  /**
   * Poproś o sumy kolumn kwot po filtrach sekcji, ze wszystkich stron (`sums=1`) — panel robi to
   * wyłącznie w widoku „Lista" i tylko dla sekcji, które mają kolumny kwot.
   */
  sums?: boolean;
  /** Poproś o firmy rekordów sekcji do filtra „Firma" (`companyOptions=1`). */
  companyOptions?: boolean;
  /** Mirror `MyPendingDecisionsQuery.companyId`. */
  companyId?: string;
}

/**
 * Wiersze sekcji kartoteki — jedno żądanie na rozwiniętą sekcję i stronę. Panel wysyła ZAWSZE
 * komplet kolumn zestawu sekcji, żeby przełączanie widoczności kolumn nie robiło nowego zapytania.
 */
export async function fetchDecisionSection<TRow extends object>(
  query: DecisionSectionQuery,
): Promise<DecisionSectionResponse<TRow>> {
  const params: Record<string, string> = { key: query.key };
  if (query.overdue) params.overdue = 'true';
  if (query.scope === 'team') params.scope = 'team';
  if (query.companyId) params.companyId = query.companyId;
  if (query.limit !== undefined) params.limit = String(query.limit);
  if (query.offset !== undefined) params.offset = String(query.offset);
  if (query.sort !== undefined) params.sort = query.sort;
  if (query.order !== undefined) params.order = query.order;
  if (query.headerFilters !== undefined) params.headerFilters = query.headerFilters;
  if (query.columns !== undefined) params.columns = query.columns;
  if (query.sums) params.sums = '1';
  if (query.companyOptions) params.companyOptions = '1';
  const res = await api.get<DecisionSectionResponse<TRow>>(
    '/processes/my-pending-decisions/section',
    { params },
  );
  return res.data;
}

// ============================================================================
// GET /api/processes/decisions/stats — liczniki kafelków kokpitu
// ============================================================================

export interface DecisionStats {
  /** Moje czekające decyzje (kroki przypisane do mnie). Cross-company. */
  pendingCount: number;
  /** Instancje zgłoszone przeze mnie (wszystkie statusy). Cross-company. */
  submittedCount: number;
  /**
   * Wszystkie instancje w wybranej firmie (cross-user). `null` gdy brak wybranej
   * firmy LUB brak uprawnienia `processes.decisions.view_all` — kafelek pokazuje
   * widok bez mylącej liczby (nie „0").
   */
  activeCount: number | null;
  /** Podzbiór `pendingCount` z przekroczonym terminem (SLA, migracja 0126). Cross-company. */
  overdueCount: number;
  /**
   * Oczekujące decyzje bezpośrednich podwładnych przełożonego (widok `?scope=team`
   * na `/my-pending-decisions`). `null` gdy user nie ma uprawnienia „podgląd
   * zespołu" (kafelek „Zespół" ukryty); liczba (także `0`) gdy uprawnienie jest,
   * ale przełożony nie ma podwładnych.
   */
  teamCount: number | null;
}

/**
 * Liczniki kafelków. Nagłówek `X-Company-Id` (gdy firma wybrana) dokłada interceptor
 * axios — w trybie „wszystkie firmy" leci bez niego, więc backend zwróci activeCount=null.
 */
export async function fetchDecisionStats(): Promise<DecisionStats> {
  const res = await api.get<DecisionStats>('/processes/decisions/stats');
  return res.data;
}

// ============================================================================
// POST /api/processes/decisions/bulk — masowe akceptacje (T17.8)
// ============================================================================

/** Typ decyzji w trybie masowym — wyłącznie akceptacja (odrzucenie to droga narysowana w procesie). */
export type BulkDecisionType = 'approve';

/**
 * Pojedyncza pozycja masowej decyzji. Panel wysyła zawsze samo „akceptuję" — serwer bierze wtedy
 * dostępną drogę domyślną etapu albo jedyną drogę etapu z definicji (drogi, która nie jest
 * domyślna, nie wybiera — ADR-0111 pkt 4). Trasa serwera przyjmuje też `routeKey` per pozycja
 * i wspólny `comment`, ale żaden ekran panelu z nich nie korzysta, więc klient ich nie wystawia.
 */
export interface BulkDecisionItem {
  stepInstanceId: string;
  decisionType: BulkDecisionType;
}

/** Status pozycji w odpowiedzi (mirror backendu). */
export type BulkItemStatus = 'done' | 'saved_waiting' | 'cancelled' | 'failed';

/**
 * Pole wymagane przez wybraną drogę albo przez proces przy starcie, którego rekord nie ma
 * (`REQUIRED_FIELDS_MISSING`) — ten sam kształt w trasie decyzji, hurtowej i startu sprawy.
 */
export interface BulkMissingField {
  slug: string;
  label: string;
}

/** Etykieta jednego elementu `missing` z odpowiedzi serwera; zły kształt elementu → pusty tekst. */
function missingFieldLabel(field: unknown): string {
  if (typeof field !== 'object' || field === null) return '';
  const { label } = field as { label?: unknown };
  return typeof label === 'string' ? label.trim() : '';
}

/**
 * Etykiety brakujących pól z odpowiedzi `REQUIRED_FIELDS_MISSING` — przycięte, bez pustych.
 * Odpowiedź serwera traktujemy jak nieufną (`unknown`): `missing` nie będące tablicą albo element
 * bez tekstowej `label` nie psują komunikatu, tylko nie wchodzą na listę.
 */
export function missingFieldLabels(missing: unknown): string[] {
  if (!Array.isArray(missing)) return [];
  return (missing as unknown[]).map(missingFieldLabel).filter((label) => label.length > 0);
}

/**
 * Komunikat odmowy STARTU sprawy z powodu pustych pól wymaganych przez proces (np. opiekun
 * dokumentu): z listą etykiet z `missing`, a gdy serwer ich nie podał (albo wszystkie są puste) —
 * ogólny tekst o polach na karcie rekordu. Okno wysyłki zostaje otwarte, więc tekst mówi, że pola
 * uzupełnia się na karcie rekordu i wysyła jeszcze raz.
 */
export function startRequiredFieldsMissingMessage(t: TFunction, missing: unknown): string {
  const fields = missingFieldLabels(missing);
  return fields.length > 0
    ? t('tenant.processes.picker.requiredFieldsMissing', { fields: fields.join(', ') })
    : t('tenant.processes.picker.requiredFieldsMissingNoList');
}

/**
 * Ostrzeżenie „dokument utworzony, ale obieg akceptacji nie wystartował" z odpowiedzi tworzenia
 * dokumentu (start automatyczny). `missing` serwer dokłada tylko przy `REQUIRED_FIELDS_MISSING`
 * (etykiety pól z katalogu pól, ten sam kształt co przy odmowie startu ręcznego).
 */
export interface ProcessWarning {
  code: string;
  missing?: BulkMissingField[];
}

/**
 * Tytuł dymku-ostrzeżenia po utworzeniu dokumentu, gdy obieg akceptacji nie wystartował (jeden
 * pomocnik dla wszystkich klas dokumentów). Każdy kod ma własny tekst
 * `tenant.documents.processWarning.<KOD>`, a kod spoza słownika dostaje tekst zastępczy.
 * `REQUIRED_FIELDS_MISSING` niesie jeszcze nazwy pól do uzupełnienia (`missing`, łączone „, "
 * tym samym pomocnikiem co przy wysyłce ręcznej); gdy serwer ich nie podał (albo wszystkie są
 * puste) — tekst bez listy.
 */
export function processWarningMessage(t: TFunction, warning: ProcessWarning): string {
  if (warning.code === 'REQUIRED_FIELDS_MISSING') {
    const fields = missingFieldLabels(warning.missing);
    return fields.length > 0
      ? t('tenant.documents.processWarning.REQUIRED_FIELDS_MISSING', { fields: fields.join(', ') })
      : t('tenant.documents.processWarning.requiredFieldsMissingNoList');
  }
  return t(`tenant.documents.processWarning.${warning.code}`, {
    defaultValue: t('tenant.documents.processWarning.fallback'),
  });
}

export interface BulkDecisionResultItem {
  stepInstanceId: string;
  status: BulkItemStatus;
  stepResult?: string;
  instanceStatus?: string;
  /** Kod błędu silnika / PERMISSION_DENIED / STEP_NOT_FOUND (gdy status=failed). */
  error?: string;
  /** `CONTROL_ERROR` / `CONTROL_WARNING` — treść procedury kontrolnej wybranej drogi. */
  message?: string;
  /**
   * `REQUIRED_FIELDS_MISSING` — pola wymagane przez wybraną drogę, których rekord nie ma, albo pole
   * „Opiekun" (kolejny etap „opiekun rekordu" na rekordzie bez opiekuna).
   */
  missing?: BulkMissingField[];
}

export interface BulkDecisionSummary {
  done: number;
  saved_waiting: number;
  cancelled: number;
  failed: number;
}

export interface BulkDecisionResponse {
  results: BulkDecisionResultItem[];
  summary: BulkDecisionSummary;
}

/**
 * Kody błędów silnika decyzji, które mają własny komunikat (adresat, droga, brak dostępnej
 * drogi, komentarz, krok już rozstrzygnięty, głos już oddany, osoba spoza akceptantów etapu,
 * sprawa nie w toku, etap nieistniejący, brak prawa do podejmowania decyzji, kolejny etap bez
 * akceptantów, rekord sprawy usunięty, moduł kartoteki sprawy nieaktywny, cztery odmowy „Cofnij
 * do poprzedniego etapu"). Zamknięta unia — mapa niżej MUSI mieć klucz dla każdego z nich.
 */
export type DecisionErrorCode =
  | 'ADDRESSEE_REQUIRED'
  | 'ADDRESSEE_NOT_IN_POOL'
  | 'ADDRESSEE_NOT_ALLOWED'
  | 'ROUTE_REQUIRED'
  | 'NO_AVAILABLE_ROUTE'
  | 'COMMENT_REQUIRED'
  | 'INVALID_ROUTE_KEY'
  | 'STEP_ALREADY_RESOLVED'
  | 'ALREADY_DECIDED'
  | 'INSTANCE_NOT_ACTIVE'
  | 'NOT_ASSIGNED'
  | 'STEP_NOT_FOUND'
  | 'PERMISSION_DENIED'
  | 'NO_APPROVERS'
  | 'TARGET_RECORD_DELETED'
  | 'TARGET_MODULE_INACTIVE'
  | 'STEP_BACK_NOT_ALLOWED'
  | 'STEP_BACK_NO_PREVIOUS'
  | 'STEP_BACK_COMMENT_REQUIRED'
  | 'STEP_BACK_NO_APPROVERS';

/**
 * Kod odmowy serwera (HTTP 404) dla sprawy, której rekord usunięto (decyzja właściciela
 * 2026-10-09): taka sprawa „nie istnieje", dopóki ktoś nie przywróci kartoteki — nic nie jest
 * zapisywane. Odpowiadają nim decyzja (pojedyncza i masowa — per pozycja), szczegóły sprawy, lista
 * adresatów i anulowanie; panel pokazuje „Nie znaleziono kartoteki." i odświeża dane procesów
 * (sprawa znika z list).
 */
export const TARGET_RECORD_DELETED_CODE = 'TARGET_RECORD_DELETED' satisfies DecisionErrorCode;

/**
 * Klucz i18n komunikatu „Nie znaleziono kartoteki." dla miejsc poza mapami decyzji (szczegóły
 * sprawy w oknie decyzji, lista adresatów, anulowanie). Masowa akceptacja ma własny klucz powodu
 * (`BULK_FAILURE_I18N_KEY`) — teksty pojedynczej decyzji i powody hurtowe są rozdzielone.
 */
export const TARGET_RECORD_DELETED_I18N_KEY = 'tenant.processes.actions.targetRecordDeleted';

/** Czy błąd z API to odmowa „rekord sprawy usunięty" (`TARGET_RECORD_DELETED`). */
export function isTargetRecordDeletedError(err: unknown): boolean {
  return extractErrorCode(err) === TARGET_RECORD_DELETED_CODE;
}

/**
 * Kod odmowy serwera (HTTP 402) dla sprawy, której rekord należy do NIEAKTYWNEGO modułu (moduł
 * Handel i Magazyn wyłączony albo rejestr własny wyłączony): taka sprawa jest po stronie serwera
 * ukryta na listach, więc panel spotyka ten kod tylko wtedy, gdy okno albo lista były otwarte,
 * zanim moduł wyłączono. Odpowiadają nim te same miejsca co przy `TARGET_RECORD_DELETED` (decyzja
 * pojedyncza i masowa — per pozycja, szczegóły sprawy, lista adresatów, anulowanie); panel pokazuje
 * „Moduł tej kartoteki jest nieaktywny." i odświeża dane procesów (sprawa znika z list). To NIE jest
 * `MODULE_NOT_ACTIVE` z ogólnej bramki modułu — ten kod ma osobną obsługę.
 */
export const TARGET_MODULE_INACTIVE_CODE = 'TARGET_MODULE_INACTIVE' satisfies DecisionErrorCode;

/** Klucz i18n komunikatu „Moduł tej kartoteki jest nieaktywny." — miejsca jak `TARGET_RECORD_DELETED_I18N_KEY`. */
export const TARGET_MODULE_INACTIVE_I18N_KEY = 'tenant.processes.actions.targetModuleInactive';

/** Czy błąd z API to odmowa „moduł kartoteki sprawy nieaktywny" (`TARGET_MODULE_INACTIVE`). */
export function isTargetModuleInactiveError(err: unknown): boolean {
  return extractErrorCode(err) === TARGET_MODULE_INACTIVE_CODE;
}

/** Kod ogólnej bramki modułu (HTTP 402) — tak odpowiada trasa sekcji przy wyłączonym module. */
const MODULE_NOT_ACTIVE_CODE = 'MODULE_NOT_ACTIVE';

/**
 * Czy błąd to odpowiedź 402 trasy SEKCJI kartoteki: moduł tej kartoteki jest nieaktywny (moduł
 * Handel i Magazyn albo moduł klasy dokumentu wyłączony po otwarciu sekcji — `MODULE_NOT_ACTIVE`;
 * na wypadek odmowy per sprawa także `TARGET_MODULE_INACTIVE`). Ponowienie ani odpytywanie tego
 * nie zmieni, więc sekcja nie ponawia i nie odświeża się w kółko, a pokazuje komunikat
 * `TARGET_MODULE_INACTIVE_I18N_KEY`. Kod musi być jednym z dwóch: 402 z innym kodem (np. nieznana
 * bramka) zostaje zwykłym błędem.
 */
export function isModuleInactiveSectionError(err: unknown): boolean {
  if (!isAxiosError(err) || err.response?.status !== 402) return false;
  const code = extractErrorCode(err);
  return code === MODULE_NOT_ACTIVE_CODE || code === TARGET_MODULE_INACTIVE_CODE;
}

/** Kod odpowiedzi 400 trasy sekcji dla klucza sekcji, którego serwer nie zna. */
const UNKNOWN_SECTION_CODE = 'UNKNOWN_SECTION';

/**
 * Czy błąd to odpowiedź 400 `UNKNOWN_SECTION` trasy SEKCJI kartoteki: serwer nie zna klucza sekcji
 * (np. kartoteki własnej, której już nie ma). Ponowienie dostanie tę samą odpowiedź, więc sekcja
 * go nie ponawia. Kod musi pasować: 400 z innym kodem (np. błędny filtr) zostaje zwykłym błędem.
 */
export function isUnknownSectionError(err: unknown): boolean {
  return (
    isAxiosError(err) &&
    err.response?.status === 400 &&
    extractErrorCode(err) === UNKNOWN_SECTION_CODE
  );
}

/**
 * Kody błędów silnika decyzji → klucz i18n komunikatu POJEDYNCZEJ decyzji (okno „Akceptacja" —
 * `AcceptanceDialog`, toast). Teksty mówią, co zrobić w tym oknie („uzupełnij komentarz i spróbuj
 * ponownie"), więc w oknie wyniku akcji masowej się nie nadają — tam te same
 * kody mają własne, krótkie powody (`BULK_FAILURE_I18N_KEY`). Kod z serwera to zwykły `string`,
 * więc w miejscu użycia sięgaj przez `decisionErrorI18nKey`, nie indeksuj mapy wprost.
 */
export const DECISION_ERROR_I18N_KEY: Readonly<Record<DecisionErrorCode, string>> = {
  ADDRESSEE_REQUIRED: 'tenant.processes.actions.addresseeRequired',
  ADDRESSEE_NOT_IN_POOL: 'tenant.processes.actions.addresseeNotInPool',
  ADDRESSEE_NOT_ALLOWED: 'tenant.processes.actions.addresseeNotAllowed',
  ROUTE_REQUIRED: 'tenant.processes.actions.routeRequired',
  // Etap bez żadnej dostępnej drogi (warunki niespełnione na danych z chwili wysłania) — nic nie
  // zapisano, sprawa zostaje w toku. Okno przy zerze dróg nie wysyła decyzji drogą (alert
  // i nieaktywne „Wykonaj"), więc ten komunikat to zabezpieczenie na wypadek odmowy serwera.
  // Okno zostaje otwarte — stan sprawy się nie zmienił, a cofnięcie (jeśli etap na nie pozwala)
  // nadal jest możliwe.
  NO_AVAILABLE_ROUTE: 'tenant.processes.actions.noAvailableRoute',
  COMMENT_REQUIRED: 'tenant.processes.actions.commentRequired',
  INVALID_ROUTE_KEY: 'tenant.processes.actions.invalidRouteKey',
  // Pięć następnych: sprawa zmieniła stan od otwarcia okna (etap rozstrzygnięty przez kogoś innego,
  // głos już oddany, osoba wypadła z akceptantów etapu, sprawa nie jest w toku, etapu nie ma) — nic
  // nie zapisano; okno się zamyka (`isDecisionCaseChangedCode`). Teksty mówią, co się zmieniło.
  STEP_ALREADY_RESOLVED: 'tenant.processes.actions.stepAlreadyResolved',
  ALREADY_DECIDED: 'tenant.processes.actions.alreadyDecided',
  INSTANCE_NOT_ACTIVE: 'tenant.processes.actions.instanceNotActive',
  NOT_ASSIGNED: 'tenant.processes.actions.notAssigned',
  STEP_NOT_FOUND: 'tenant.processes.actions.stepNotFound',
  // Brak prawa „Podejmowanie decyzji" w firmie rekordu sprawy — jedyny z nowych kodów, po którym
  // okno ZOSTAJE otwarte (stan sprawy się nie zmienił; to kwestia uprawnień osoby, nie sprawy).
  PERMISSION_DENIED: 'tenant.processes.actions.permissionDenied',
  // Ogólny tekst bez nazw etapów — z nazwami (gdy serwer je podał) buduje `noApproversMessage`.
  NO_APPROVERS: 'tenant.processes.actions.noApprovers',
  // Rekord sprawy usunięty — decyzji „nie ma" (nic nie zapisano); okno się zamyka.
  TARGET_RECORD_DELETED: TARGET_RECORD_DELETED_I18N_KEY,
  // Moduł kartoteki sprawy nieaktywny — decyzji „nie ma" (nic nie zapisano); okno się zamyka.
  TARGET_MODULE_INACTIVE: TARGET_MODULE_INACTIVE_I18N_KEY,
  // „Cofnij do poprzedniego etapu" odrzucone (nic nie zapisano; stan sprawy się nie zmienił, więc
  // okno ZOSTAJE otwarte — osoba może wybrać drogę). Dwa pierwsze to nieaktualna opcja w oknie
  // (etap bez cofania / bez poprzedniego etapu), brak komentarza to pole do uzupełnienia; z nazwą
  // etapu komunikat o braku osób buduje `stepBackNoApproversMessage`.
  STEP_BACK_NOT_ALLOWED: 'tenant.processes.actions.stepBackNotAllowed',
  STEP_BACK_NO_PREVIOUS: 'tenant.processes.actions.stepBackNoPrevious',
  STEP_BACK_COMMENT_REQUIRED: 'tenant.processes.actions.stepBackCommentRequired',
  STEP_BACK_NO_APPROVERS: 'tenant.processes.actions.stepBackNoApprovers',
};

/** Czy kod z serwera to jeden ze znanych kodów błędu decyzji (własny klucz mapy, nie odziedziczony). */
export function isDecisionErrorCode(code: string): code is DecisionErrorCode {
  return Object.prototype.hasOwnProperty.call(DECISION_ERROR_I18N_KEY, code);
}

/** Klucz i18n komunikatu dla kodu błędu decyzji z serwera; `undefined` = kod nieznany (ogólny błąd). */
export function decisionErrorI18nKey(code: string | undefined): string | undefined {
  return code !== undefined && isDecisionErrorCode(code)
    ? DECISION_ERROR_I18N_KEY[code]
    : undefined;
}

/**
 * Kody odmowy decyzji, po których sprawa „nie jest już taka, jak w momencie otwarcia okna": etap
 * rozstrzygnięty przez kogoś innego, głos już oddany, osoba wypadła z akceptantów, sprawa nie
 * jest w toku, etapu nie ma, rekord sprawy usunięty albo moduł jego kartoteki wyłączony. Nic nie
 * zapisano, a ponowienie dostanie tę samą odpowiedź — okno decyzji zamyka się, a dane procesów
 * są odświeżane (wiersz / panel znika albo pokazuje nowy krok). `PERMISSION_DENIED` celowo tu NIE
 * należy: to brak prawa osoby w firmie rekordu, nie zmiana stanu sprawy — okno zostaje otwarte.
 * Zbiór kluczowany `DecisionErrorCode`, więc literówka w kodzie wywali typecheck.
 */
const DECISION_CASE_CHANGED_CODES: ReadonlySet<DecisionErrorCode> = new Set<DecisionErrorCode>([
  'STEP_ALREADY_RESOLVED',
  'ALREADY_DECIDED',
  'INSTANCE_NOT_ACTIVE',
  'NOT_ASSIGNED',
  'STEP_NOT_FOUND',
  'TARGET_RECORD_DELETED',
  'TARGET_MODULE_INACTIVE',
]);

/** Czy kod z serwera to odmowa „sprawa zmieniła stan od otwarcia okna" (okno się zamyka, dane się odświeżają). */
export function isDecisionCaseChangedCode(code: string | undefined): boolean {
  return code !== undefined && isDecisionErrorCode(code) && DECISION_CASE_CHANGED_CODES.has(code);
}

/**
 * Kody odmowy STARTU sprawy (trasa startu), które mają własny komunikat. `NO_APPROVERS` tu nie
 * ma — jego komunikat z nazwami etapów buduje `noApproversMessage`. Zamknięta unia — mapa niżej
 * MUSI mieć klucz dla każdego z nich.
 */
export type StartErrorCode =
  | 'INVALID_START_ROUTE'
  | 'ADDRESSEE_REQUIRED'
  | 'ADDRESSEE_NOT_IN_POOL'
  | 'ADDRESSEE_NOT_ALLOWED'
  | 'TEMPLATE_UNAVAILABLE'
  | 'RECORD_HAS_INSTANCE'
  | 'INSTANCE_ALREADY_ACTIVE'
  | 'PROCESS_NOT_PUBLISHED'
  | 'PROCESS_INACTIVE'
  | 'PROCESS_NOT_APPLICABLE_TO_RECORD';

/**
 * Kody odmowy startu → klucz i18n komunikatu (toast przycisku „Wyślij do akceptacji"). Kod
 * z serwera to zwykły `string` — w miejscu użycia sięgaj przez `startErrorI18nKey`. Każdy
 * komunikat mówi, co się stało i co zrobić, a kończy się informacją, że nic nie zapisano
 * (start jest atomowy — odmowa cofa całą transakcję).
 */
export const START_ERROR_I18N_KEY: Readonly<Record<StartErrorCode, string>> = {
  // Ze startu nie wychodzi dokładnie jedna droga do etapu akceptacji albo rozdzielenia (graf
  // zmieniony po publikacji) — sprawa nie powstała.
  INVALID_START_ROUTE: 'tenant.processes.picker.invalidStartRoute',
  // Pierwszy etap wymaga wskazania adresata, a okno wysyłki go nie przekazało (pula zmieniona
  // po otwarciu okna) albo wskazana osoba nie należy już do puli akceptantów etapu.
  ADDRESSEE_REQUIRED: 'tenant.processes.picker.addresseeRequired',
  ADDRESSEE_NOT_IN_POOL: 'tenant.processes.picker.addresseeNotInPool',
  // Ustawienia pierwszego etapu zmieniły się po otwarciu okna tak, że wskazanie jednej osoby
  // przestało być dozwolone (etap głosuje „wszyscy" / „większość").
  ADDRESSEE_NOT_ALLOWED: 'tenant.processes.picker.addresseeNotAllowed',
  // Proces podpięty do wzoru centralnego (ITE), którego nie da się użyć (usunięty,
  // niepublikowany albo uszkodzony) — poprawić go może tylko administrator.
  TEMPLATE_UNAVAILABLE: 'tenant.processes.picker.templateUnavailable',
  // Rekord ma już sprawę (aktywną albo zakończoną; drugi kod = wyścig dwóch równoległych
  // startów) — jeden tekst na oba kody: użytkownik potrzebuje tej samej wskazówki.
  RECORD_HAS_INSTANCE: 'tenant.processes.picker.recordHasInstance',
  INSTANCE_ALREADY_ACTIVE: 'tenant.processes.picker.recordHasInstance',
  // Proces wycofany z publikacji albo wyłączony od chwili pobrania listy w oknie wysyłki.
  PROCESS_NOT_PUBLISHED: 'tenant.processes.picker.processNotPublished',
  PROCESS_INACTIVE: 'tenant.processes.picker.processInactive',
  // Proces nie obejmuje typu ani kategorii tego rekordu (albo ma pustą listę dozwolonych) —
  // lista procesów w oknie była nieaktualna albo ustawienia procesu zmieniono po jej pobraniu.
  PROCESS_NOT_APPLICABLE_TO_RECORD: 'tenant.processes.picker.processNotApplicable',
};

/** Czy kod z serwera to jeden ze znanych kodów odmowy startu (własny klucz mapy, nie odziedziczony). */
function isStartErrorCode(code: string): code is StartErrorCode {
  return Object.prototype.hasOwnProperty.call(START_ERROR_I18N_KEY, code);
}

/** Klucz i18n komunikatu dla kodu odmowy startu z serwera; `undefined` = kod nieznany (ogólny błąd). */
export function startErrorI18nKey(code: string | undefined): string | undefined {
  return code !== undefined && isStartErrorCode(code) ? START_ERROR_I18N_KEY[code] : undefined;
}

/**
 * Komunikat dla `NO_APPROVERS` (typ etapu `NoApproversStage` ze `@saas/shared`): z nazwami
 * etapów, gdy serwer je podał (`stages` z odpowiedzi) — każda nazwa w osobnych cudzysłowach
 * (lokalizowanych), liczba mnoga według liczby WSZYSTKICH etapów. Etapy bez nazwy nie znikają
 * z komunikatu: dopisujemy „oraz N etapów bez nazwy". Gdy żaden etap nie ma nazwy (albo serwer
 * nie podał etapów), zostaje ogólny tekst o co najmniej jednym etapie.
 *
 * Lista nazw ma spójnik właściwy językowi tłumaczenia („A, B i C” / „A, B, and C”). Język bierzemy
 * z samego tłumaczenia (`returnDetails` → `usedLng`), bo `t` z `useTranslation` nie niesie
 * języka — to ten, z którego faktycznie pochodzi tekst (także przy powrocie do języka zapasowego).
 * Gdy jest dopisek o etapach bez nazwy, ma on własny spójnik („oraz” / „and”), więc nazwy łączy
 * zwykły przecinek — druga lista ze spójnikiem dałaby „A i B oraz 1 etap bez nazwy”. Przecinek
 * przed dopiskiem wybiera klucz `noApproversUnnamedList` z liczbą mnogą po liczbie NAZWANYCH
 * etapów: po angielsku „“A” and 1 unnamed stage”, ale „“A”, “B”, and 1 unnamed stage”.
 */
export function noApproversMessage(
  t: TFunction,
  stages: readonly NoApproversStage[] | undefined,
): string {
  const all = stages ?? [];
  const labels = all.map((s) => s.label?.trim() ?? '').filter((label) => label.length > 0);
  if (labels.length === 0) return t('tenant.processes.actions.noApprovers');

  const quoted = labels.map((name) =>
    t('tenant.processes.actions.noApproversStageName', { name, returnDetails: true }),
  );
  const names = quoted.map((q) => q.res);
  const unnamed = all.length - labels.length;
  // Przy etapach bez nazwy dopisek („oraz 1 etap bez nazwy”) dokleja klucz z liczbą mnogą po
  // liczbie NAZWANYCH etapów — po angielsku przecinek przed „and” tylko przy ≥ 2 nazwach,
  // tak jak w `Intl.ListFormat` dla samych nazw.
  const listed =
    unnamed > 0
      ? t('tenant.processes.actions.noApproversUnnamedList', {
          names: names.join(', '),
          unnamed: t('tenant.processes.actions.noApproversUnnamed', { count: unnamed }),
          count: names.length,
        })
      : new Intl.ListFormat(quoted[0]?.usedLng, { style: 'long', type: 'conjunction' }).format(
          names,
        );
  return t('tenant.processes.actions.noApproversStages', { stages: listed, count: all.length });
}

/**
 * Komunikat dla `STEP_BACK_NO_APPROVERS` (422): nikt z osób z poprzedniego etapu nie jest już
 * dostępny, więc „Cofnij do poprzedniego etapu" jest odrzucone. Serwer podaje etap w `stages`
 * (ten sam kształt co przy `NO_APPROVERS`); z nazwą etapu komunikat jest konkretniejszy, bez
 * nazwy (albo bez `stages`) zostaje tekst ogólny. To NIE jest `noApproversMessage`: tamten mówi
 * o błędnie skonfigurowanym procesie („poproś administratora o uzupełnienie ustawień"), a tu
 * ustawienia są poprawne — osoby po prostu stały się niedostępne.
 */
export function stepBackNoApproversMessage(
  t: TFunction,
  stages: readonly NoApproversStage[] | undefined,
): string {
  const stage = stages?.find((s) => (s.label?.trim() ?? '').length > 0)?.label?.trim();
  return stage
    ? t('tenant.processes.actions.stepBackNoApproversStage', { stage })
    : t('tenant.processes.actions.stepBackNoApprovers');
}

/**
 * Kod nadawany PO STRONIE PANELU (nie przez serwer) pozycjom, których paczka nie została wysłana
 * albo nie dotarł jej wynik — patrz `bulkSubmitDecisionsInBatches`.
 */
export const BULK_BATCH_FAILED_CODE = 'BATCH_REQUEST_FAILED';

/**
 * F.4/F.5 (ADR-0073) + procedury kontrolne (ADR-0072) — mapowanie kodu błędu per-pozycja z bulk
 * API na klucz i18n powodu pominięcia w oknie wyniku (`BulkAcceptResultDialog`, przez
 * `bulkSkipReason`).
 *
 * Powody są pisane POD LISTĘ WYNIKÓW, nie pod okno decyzji: okno wyniku nie ma pól do
 * uzupełnienia ani widoku do odświeżenia, więc teksty pojedynczej decyzji
 * (`DECISION_ERROR_I18N_KEY`: „uzupełnij komentarz i spróbuj ponownie", „wskaż adresata")
 * nie mogłyby tam być wykonane. Tu: gdzie potrzebna jest interakcja, powód
 * odsyła do osobnej decyzji w oknie sprawy; gdzie stan sprawy zmienił się w międzyczasie, mówi
 * po prostu, co się zmieniło. Procedury kontrolne (`CONTROL_*`) mają teksty o DECYZJI, nie o zapisie
 * dokumentu: blokada → ten sam tekst co w oknie decyzji (`tenant.processes.actions.controlError`),
 * ostrzeżenie bez treści od procedury → powód odsyłający do okna decyzji (ostrzeżenia nie da się
 * potwierdzić hurtem); komunikat od procedury (`message`) dokleja `bulkSkipReason`. Awaria
 * procedury (`CONTROL_HOOK_FAILED`) reużywa tekst `useControlWarning` — ten nie zależy od okna.
 *
 * Mapa obejmuje każdy kod, który trasa hurtowa potrafi zwrócić per pozycja. Poza nią zostają
 * kody, których panel nie wywoła (`ADDRESSEE_NOT_IN_POOL`/`ADDRESSEE_NOT_ALLOWED` — panel nie
 * wysyła adresatów, `INVALID_ROUTE_KEY` — panel nie wysyła drogi, a drogi etapu liczą się
 * z danych zamrożonych przy starcie sprawy, więc nie znikają, `INVALID_DECISION_TYPE` — wysyła
 * tylko „akceptuję") i błędy danych docelowych — `bulkSkipReason` pokazuje dla nich ogólny powód
 * zamiast surowego kodu. Hurt nie wybiera za człowieka drogi, która nie jest domyślna
 * (ADR-0111 pkt 4): `ROUTE_REQUIRED` dostaje etap bez dostępnej drogi domyślnej — także gdy
 * z kilku dróg widać tylko jedną — a `NO_AVAILABLE_ROUTE` etap bez żadnej dostępnej drogi.
 *
 * Kod z serwera to zwykły `string`, a mapa to zwykły obiekt — w miejscu użycia sięgaj przez
 * `bulkFailureI18nKey`, nie indeksuj wprost (kod `toString` / `constructor` zwróciłby funkcję
 * odziedziczoną po `Object.prototype`).
 */
export const BULK_FAILURE_I18N_KEY: Partial<Record<string, string>> = {
  // Sprawa wymaga interakcji, której hurt nie umie wykonać — powód odsyła do okna decyzji.
  COMMENT_REQUIRED: 'tenant.processes.approvals.bulk.reason.commentRequired',
  ADDRESSEE_REQUIRED: 'tenant.processes.approvals.bulk.reason.addressee',
  ROUTE_REQUIRED: 'tenant.processes.approvals.bulk.reason.routeRequired',
  // Etap bez żadnej dostępnej drogi — pozycja nie zapisana, sprawa zostaje w toku; powód mówi, co
  // dalej (anulowanie, poprawa kartoteki i ponowne wysłanie).
  NO_AVAILABLE_ROUTE: 'tenant.processes.approvals.bulk.reason.noAvailableRoute',
  // Stan sprawy zmienił się między wyświetleniem listy a wysyłką (ktoś rozstrzygnął etap,
  // oddano już głos, odebrano uprawnienie, sprawa zakończona) — powód mówi, co się zmieniło.
  STEP_ALREADY_RESOLVED: 'tenant.processes.approvals.bulk.reason.stepAlreadyResolved',
  ALREADY_DECIDED: 'tenant.processes.approvals.bulk.reason.alreadyDecided',
  NOT_ASSIGNED: 'tenant.processes.approvals.bulk.reason.notAssigned',
  INSTANCE_NOT_ACTIVE: 'tenant.processes.approvals.bulk.reason.instanceNotActive',
  PERMISSION_DENIED: 'tenant.processes.approvals.bulk.reason.permissionDenied',
  STEP_NOT_FOUND: 'tenant.processes.approvals.bulk.reason.stepNotFound',
  // Rekord sprawy usunięty w międzyczasie — sprawa „nie istnieje" i znika z listy po odświeżeniu.
  TARGET_RECORD_DELETED: 'tenant.processes.approvals.bulk.reason.targetRecordDeleted',
  // Moduł kartoteki sprawy wyłączony w międzyczasie — serwer ukrywa taką sprawę na listach, więc
  // znika po odświeżeniu tak samo jak sprawa z usuniętym rekordem.
  TARGET_MODULE_INACTIVE: 'tenant.processes.approvals.bulk.reason.targetModuleInactive',
  // Nieoczekiwany błąd serwera przy tej pozycji (reszta paczki mogła przejść).
  INTERNAL_ERROR: 'tenant.processes.approvals.bulk.reason.internalError',
  // Pozycja cofnięta, ale reszta paczki mogła przejść — tekst pojedynczej decyzji („Nic nie
  // zostało zapisane") wprowadzałby w błąd.
  NO_APPROVERS: 'tenant.processes.actions.noApproversBulk',
  // Droga wymaga pól, których rekord nie ma — pojedyncza decyzja pokazuje je alertem w oknie,
  // masowo lista pól wchodzi do powodu pominięcia (`{{fields}}`, buduje `bulkSkipReason`).
  REQUIRED_FIELDS_MISSING: 'tenant.processes.approvals.bulk.reason.requiredFieldsMissing',
  // Blokada procedury kontrolnej — ten sam tekst co w oknie decyzji; treść od procedury
  // (`message`) dokleja `bulkSkipReason`.
  CONTROL_ERROR: 'tenant.processes.actions.controlError',
  // Ostrzeżenie BEZ treści od procedury (z treścią: osobny tekst z `{{message}}` w
  // `bulkSkipReason`) — hurtem go nie potwierdzimy, powód odsyła do okna decyzji.
  CONTROL_WARNING: 'tenant.processes.approvals.bulk.reason.controlWarningNoMessage',
  CONTROL_HOOK_FAILED: 'tenant.documents.controlHookFailed',
  // Kod trasy hurtowej (nie silnika): decyzja zapisana, ale sprawa nie miała dokąd pójść
  // i przeszła w stan błędu (wadliwa definicja — scalenie bez wyjścia, rozdzielenie bez gałęzi
  // albo scalenie czekające na gałąź bez otwartego etapu).
  INSTANCE_ERRORED: 'tenant.processes.actions.instanceErrored',
  // Kod nadawany przez panel: paczka z tą pozycją nie została wysłana / nie znamy jej wyniku.
  [BULK_BATCH_FAILED_CODE]: 'tenant.processes.approvals.bulk.reason.batchFailed',
};

/** Klucz i18n powodu porażki pozycji bulk dla kodu z serwera; `undefined` = kod spoza mapy. */
export function bulkFailureI18nKey(code: string | undefined): string | undefined {
  return code !== undefined && Object.prototype.hasOwnProperty.call(BULK_FAILURE_I18N_KEY, code)
    ? BULK_FAILURE_I18N_KEY[code]
    : undefined;
}

/**
 * Powód pominięcia pozycji akcji masowej, gotowy do pokazania w oknie wyniku (zawsze tekst po
 * polsku/angielsku, nigdy surowy kod).
 *
 * - `cancelled` — sprawa auto-anulowana, bo kartoteka została w międzyczasie usunięta.
 * - `REQUIRED_FIELDS_MISSING` — lista brakujących pól (etykiety z `missing`).
 * - `CONTROL_ERROR` — tekst blokady decyzji z mapy + treść procedury kontrolnej (`message`).
 * - `CONTROL_WARNING` — ostrzeżenia procedury NIE da się potwierdzić hurtem, więc powód mówi to
 *   wprost i niesie treść ostrzeżenia (bez treści: powód z mapy, odsyłający do okna decyzji).
 * - pozostałe kody z mapy — ich tekst; kod spoza mapy — ogólny powód.
 */
export function bulkSkipReason(t: TFunction, item: BulkDecisionResultItem): string {
  if (item.status === 'cancelled') return t('tenant.processes.approvals.bulk.reason.cancelled');

  const code = item.error;
  const message = item.message?.trim() ?? '';

  if (code === 'REQUIRED_FIELDS_MISSING') {
    const fields = missingFieldLabels(item.missing);
    return fields.length > 0
      ? t('tenant.processes.approvals.bulk.reason.requiredFieldsMissing', {
          fields: fields.join(', '),
        })
      : t('tenant.processes.approvals.bulk.reason.requiredFieldsMissingNoList');
  }

  if (code === 'CONTROL_WARNING' && message.length > 0) {
    return t('tenant.processes.approvals.bulk.reason.controlWarning', { message });
  }

  const key = bulkFailureI18nKey(code);
  if (key === undefined) return t('tenant.processes.approvals.bulk.reason.unknown');

  const base = t(key);
  return code === 'CONTROL_ERROR' && message.length > 0 ? `${base} ${message}` : base;
}

/**
 * Wysyła masową akceptację dla listy kroków — każda pozycja to samo „akceptuję", serwer bierze
 * dostępną drogę domyślną etapu albo jedyną drogę etapu z definicji (inaczej pozycja wraca jako
 * pominięta). Trasa serwera przyjmuje też `routeKey` per pozycja i wspólny `comment`
 * doklejany do każdej pozycji, ale panel z nich nie korzysta, więc ten klient ich nie wystawia
 * (decyzje wymagające drogi albo komentarza wracają jako pominięte i rozstrzyga się je w oknie
 * decyzji). Backend zwraca status per pozycja — porażka jednej nie wywala reszty (zawsze 200).
 * Maksymalnie `BULK_MAX_ITEMS_PER_REQUEST` pozycji na żądanie — dłuższe listy wysyłaj przez
 * `bulkSubmitDecisionsInBatches`.
 */
export async function bulkSubmitDecisions(
  items: BulkDecisionItem[],
): Promise<BulkDecisionResponse> {
  const res = await api.post<BulkDecisionResponse>('/processes/decisions/bulk', { items });
  return res.data;
}

/**
 * Limit serwera: więcej niż tyle pozycji w żądaniu → 422 odrzucające CAŁE żądanie. Jedno źródło
 * (`PROCESS_BULK_DECISIONS_MAX_ITEMS`) dla walidatora serwera i tego klienta.
 */
export const BULK_MAX_ITEMS_PER_REQUEST = PROCESS_BULK_DECISIONS_MAX_ITEMS;

/** Licznik statusów pozycji (to samo, co serwer zwraca w `summary` jednego żądania). */
export function summarizeBulkResults(
  results: readonly BulkDecisionResultItem[],
): BulkDecisionSummary {
  const summary: BulkDecisionSummary = { done: 0, saved_waiting: 0, cancelled: 0, failed: 0 };
  for (const result of results) summary[result.status] += 1;
  return summary;
}

/**
 * Masowa akceptacja dowolnej liczby pozycji: paczki po `BULK_MAX_ITEMS_PER_REQUEST`, jedna po
 * drugiej (kolejna dopiero po odpowiedzi poprzedniej), z JEDNYM wspólnym wynikiem w kolejności
 * pozycji.
 *
 * Gdy żądanie padnie (sieć/serwer): jeśli to PIERWSZA paczka, nic nie zostało przetworzone —
 * błąd leci do wołającego. Jeśli wcześniejsze paczki już poszły, ich wyniki zostają, a pozycje
 * tej i następnych paczek wracają jako `failed` z kodem `BULK_BATCH_FAILED_CODE` (jawnie pominięte,
 * kolejnych paczek nie wysyłamy) — bez tego user nie wiedziałby, co już zostało zaakceptowane.
 */
export async function bulkSubmitDecisionsInBatches(
  items: BulkDecisionItem[],
): Promise<BulkDecisionResponse> {
  const results: BulkDecisionResultItem[] = [];
  for (let start = 0; start < items.length; start += BULK_MAX_ITEMS_PER_REQUEST) {
    try {
      const response = await bulkSubmitDecisions(
        items.slice(start, start + BULK_MAX_ITEMS_PER_REQUEST),
      );
      results.push(...response.results);
    } catch (error) {
      if (start === 0) throw error;
      for (const item of items.slice(start)) {
        results.push({
          stepInstanceId: item.stepInstanceId,
          status: 'failed',
          error: BULK_BATCH_FAILED_CODE,
        });
      }
      break;
    }
  }
  return { results, summary: summarizeBulkResults(results) };
}

// ============================================================================
// GET /api/processes/my-submitted-instances — moje zgłoszone instancje
// ============================================================================

/** Instance-level row — wspólny shape dla `my-submitted` i `instances/all`. */
export interface InstanceListingRow {
  id: string;
  instanceNumber: number;
  processId: string;
  targetModuleCode: string;
  targetRecordId: string;
  submittedByUserId: string;
  submittedAt: string;
  completedAt: string | null;
  status: InstanceStatus;
  currentNodeIds: string[] | null;
  /** Nazwa etapu końcowego zakończonej sprawy (np. „Zaakceptowano"); `null` gdy sprawa nie jest zakończona. */
  finalNodeLabel: string | null;
  companyId: string | null;
  // Enrichment:
  processName: string | null;
  currentNodeLabels: Array<string | null>;
  targetLabel: string | null;
  submittedByUser: EnrichedSubmitterInfo | null;
}

export interface InstancesListResponse {
  data: InstanceListingRow[];
  total: number;
  limit: number;
  offset: number;
}

export interface MySubmittedInstancesQuery {
  status?: InstanceStatus;
  limit?: number;
  offset?: number;
  /**
   * Kreator Listy — filtr per kolumna z popupu nagłówka `InstanceListingTable`.
   * JSON.stringify `HeaderFilter[]` (mirror `serializeHeaderFiltersForRequest` w
   * `InstanceListingTable.tsx`). Whitelist backendu →
   * `HEADER_FILTER_FIELDS` w `apps/api/app/api/processes/my-submitted-instances/route.ts`.
   */
  headerFilters?: string;
  /** Sortowanie `<pole>:<kierunek>` (np. `process_name:asc`). Whitelist backendu →
   *  `sortFields` w `apps/api/app/api/processes/my-submitted-instances/route.ts`. */
  sort?: string;
  /**
   * Wymiar grupowania (`?groupBy=`) — biała lista `SUBMITTED_INSTANCE_GROUP_BY` z `@saas/shared`
   * (proces/kartoteka/status). Brak → lista płaska (dotychczasowe zachowanie). Patrz komentarz
   * nagłówkowy sekcji „tryb grupowany" wyżej.
   */
  groupBy?: DecisionGroupBy;
}

export async function fetchMySubmittedInstances(
  query: MySubmittedInstancesQuery = {},
): Promise<InstancesListResponse> {
  const params: Record<string, string> = {};
  if (query.status !== undefined) params.status = query.status;
  if (query.limit !== undefined) params.limit = String(query.limit);
  if (query.offset !== undefined) params.offset = String(query.offset);
  if (query.headerFilters !== undefined) params.headerFilters = query.headerFilters;
  if (query.sort !== undefined) params.sort = query.sort;
  const res = await api.get<InstancesListResponse>('/processes/my-submitted-instances', { params });
  return res.data;
}

export interface GroupedSubmittedInstancesResponse {
  groups: DecisionGroupHeader[];
  groupBy: DecisionGroupBy;
  total: number;
}

export interface GroupedSubmittedInstancesQuery {
  groupBy: DecisionGroupBy;
  status?: InstanceStatus;
  headerFilters?: string;
}

/** Nagłówki koszyków `GET /api/processes/my-submitted-instances?groupBy=` (bez `groupKey`). */
export async function fetchGroupedSubmittedInstances(
  query: GroupedSubmittedInstancesQuery,
): Promise<GroupedSubmittedInstancesResponse> {
  const params: Record<string, string> = { groupBy: query.groupBy };
  if (query.status !== undefined) params.status = query.status;
  if (query.headerFilters !== undefined) params.headerFilters = query.headerFilters;
  const res = await api.get<GroupedSubmittedInstancesResponse>(
    '/processes/my-submitted-instances',
    {
      params,
    },
  );
  return res.data;
}

export interface SubmittedInstanceGroupRowsQuery {
  groupBy: DecisionGroupBy;
  /** Klucz koszyka do rozwinięcia — z `DecisionGroupHeader.key`, wysyłany DOSŁOWNIE. */
  groupKey: string;
  status?: InstanceStatus;
  limit?: number;
  offset?: number;
  headerFilters?: string;
  sort?: string;
}

/** Rozwinięcie JEDNEGO koszyka `GET /api/processes/my-submitted-instances?groupBy=&groupKey=`. */
export async function fetchSubmittedInstanceGroupRows(
  query: SubmittedInstanceGroupRowsQuery,
): Promise<InstancesListResponse> {
  const params: Record<string, string> = { groupBy: query.groupBy, groupKey: query.groupKey };
  if (query.status !== undefined) params.status = query.status;
  if (query.limit !== undefined) params.limit = String(query.limit);
  if (query.offset !== undefined) params.offset = String(query.offset);
  if (query.headerFilters !== undefined) params.headerFilters = query.headerFilters;
  if (query.sort !== undefined) params.sort = query.sort;
  const res = await api.get<InstancesListResponse>('/processes/my-submitted-instances', { params });
  return res.data;
}

// ============================================================================
// GET /api/processes/instances/all — cross-user (admin view)
// ============================================================================

export interface AllInstancesQuery {
  status?: InstanceStatus;
  processId?: string;
  limit?: number;
  offset?: number;
  /**
   * Kreator Listy — filtr per kolumna z popupu nagłówka `InstanceListingTable`.
   * JSON.stringify `HeaderFilter[]` (mirror `serializeHeaderFiltersForRequest` w
   * `InstanceListingTable.tsx`). Whitelist backendu →
   * `HEADER_FILTER_FIELDS` w `apps/api/app/api/processes/instances/all/route.ts`.
   */
  headerFilters?: string;
  /** Sortowanie `<pole>:<kierunek>` (np. `submitted_by:asc`). Whitelist backendu →
   *  `sortFields` w `apps/api/app/api/processes/instances/all/route.ts`. */
  sort?: string;
  /**
   * Wymiar grupowania (`?groupBy=`) — biała lista `ACTIVE_INSTANCE_GROUP_BY` z `@saas/shared`
   * (proces/zgłaszający/kartoteka/status). Brak → lista płaska (dotychczasowe zachowanie).
   */
  groupBy?: DecisionGroupBy;
}

export async function fetchAllInstances(
  query: AllInstancesQuery = {},
): Promise<InstancesListResponse> {
  const params: Record<string, string> = {};
  if (query.status !== undefined) params.status = query.status;
  if (query.processId !== undefined) params.processId = query.processId;
  if (query.limit !== undefined) params.limit = String(query.limit);
  if (query.offset !== undefined) params.offset = String(query.offset);
  if (query.headerFilters !== undefined) params.headerFilters = query.headerFilters;
  if (query.sort !== undefined) params.sort = query.sort;
  const res = await api.get<InstancesListResponse>('/processes/instances/all', {
    params,
  });
  return res.data;
}

export interface GroupedAllInstancesResponse {
  groups: DecisionGroupHeader[];
  groupBy: DecisionGroupBy;
  total: number;
}

export interface GroupedAllInstancesQuery {
  groupBy: DecisionGroupBy;
  status?: InstanceStatus;
  processId?: string;
  headerFilters?: string;
}

/** Nagłówki koszyków `GET /api/processes/instances/all?groupBy=` (bez `groupKey`). */
export async function fetchGroupedAllInstances(
  query: GroupedAllInstancesQuery,
): Promise<GroupedAllInstancesResponse> {
  const params: Record<string, string> = { groupBy: query.groupBy };
  if (query.status !== undefined) params.status = query.status;
  if (query.processId !== undefined) params.processId = query.processId;
  if (query.headerFilters !== undefined) params.headerFilters = query.headerFilters;
  const res = await api.get<GroupedAllInstancesResponse>('/processes/instances/all', { params });
  return res.data;
}

export interface ActiveInstanceGroupRowsQuery {
  groupBy: DecisionGroupBy;
  /** Klucz koszyka do rozwinięcia — z `DecisionGroupHeader.key`, wysyłany DOSŁOWNIE. */
  groupKey: string;
  status?: InstanceStatus;
  processId?: string;
  limit?: number;
  offset?: number;
  headerFilters?: string;
  sort?: string;
}

/** Rozwinięcie JEDNEGO koszyka `GET /api/processes/instances/all?groupBy=&groupKey=`. */
export async function fetchActiveInstanceGroupRows(
  query: ActiveInstanceGroupRowsQuery,
): Promise<InstancesListResponse> {
  const params: Record<string, string> = { groupBy: query.groupBy, groupKey: query.groupKey };
  if (query.status !== undefined) params.status = query.status;
  if (query.processId !== undefined) params.processId = query.processId;
  if (query.limit !== undefined) params.limit = String(query.limit);
  if (query.offset !== undefined) params.offset = String(query.offset);
  if (query.headerFilters !== undefined) params.headerFilters = query.headerFilters;
  if (query.sort !== undefined) params.sort = query.sort;
  const res = await api.get<InstancesListResponse>('/processes/instances/all', { params });
  return res.data;
}

/**
 * Helper — wyświetla labelkę zlecającego z fallbackiem.
 * `displayName ?? username ?? email ?? '(nieznany)'`.
 */
export function displaySubmitter(submitter: EnrichedSubmitterInfo | null): string {
  if (!submitter) return '';
  return submitter.displayName || submitter.username || submitter.email || '';
}
